import React, { createContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import CenterAlert from "../Components/CenterAlert";

export const CartContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const CartProvider = ({ children }) => {
  const getStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch (error) {
      return null;
    }
  };

  const getToken = () => localStorage.getItem("token");

  const getUserId = () => {
    const user = getStoredUser();
    return user?._id || user?.id || "guest";
  };

  const getCartStorageKey = () => `cartItems_${getUserId()}`;
  const getCouponStorageKey = () => `cartCoupon_${getUserId()}`;
  const guestCartKey = "cartItems_guest";

  const [cartKey, setCartKey] = useState(getCartStorageKey());
  const [cartItems, setCartItems] = useState(() => {
    try {
      const storedCart = localStorage.getItem(getCartStorageKey());
      return storedCart ? JSON.parse(storedCart) : [];
    } catch (error) {
      return [];
    }
  });

  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);

  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });

  const triggerAlert = (type, title, message) => {
    setAlertConfig({ isOpen: true, type, title, message });
  };

  const closeAlert = () => {
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const isAuthenticated = () => !!getToken();

  const getItemId = (item) =>
    item?._id || item?.id || item?.product?._id || item?.productId?._id || item?.productId;

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  const persistCouponState = ({
    nextCouponCode = couponCode,
    nextDiscount = discount,
    nextCouponMessage = couponMessage,
    nextCouponApplied = couponApplied,
  } = {}) => {
    try {
      localStorage.setItem(
        getCouponStorageKey(),
        JSON.stringify({
          couponCode: nextCouponCode,
          discount: nextDiscount,
          couponMessage: nextCouponMessage,
          couponApplied: nextCouponApplied,
        })
      );
    } catch (error) {}
  };

  const loadCouponState = () => {
    try {
      const savedCoupon = JSON.parse(
        localStorage.getItem(getCouponStorageKey()) || "null"
      );

      if (savedCoupon) {
        setCouponCode(savedCoupon.couponCode || "");
        setDiscount(Number(savedCoupon.discount || 0));
        setCouponMessage(savedCoupon.couponMessage || "");
        setCouponApplied(Boolean(savedCoupon.couponApplied));
      } else {
        setCouponCode("");
        setDiscount(0);
        setCouponMessage("");
        setCouponApplied(false);
      }
    } catch (error) {
      setCouponCode("");
      setDiscount(0);
      setCouponMessage("");
      setCouponApplied(false);
    }
  };

  const removeCoupon = (showToast = false) => {
    setCouponCode("");
    setDiscount(0);
    setCouponMessage("");
    setCouponApplied(false);
    setCouponLoading(false);

    try {
      localStorage.removeItem(getCouponStorageKey());
    } catch (error) {}

    if (showToast) {
      toast.success("Coupon removed");
    }
  };

  const normalizeCartItems = (data) => {
    const rawItems =
      data?.items ||
      data?.cart?.items ||
      data?.data?.items ||
      (Array.isArray(data) ? data : []) ||
      [];

    if (!Array.isArray(rawItems)) return [];

    return rawItems
      .map((item) => {
        const product =
          item?.product && typeof item.product === "object"
            ? item.product
            : item?.productId && typeof item.productId === "object"
            ? item.productId
            : null;

        if (!product?._id) return null;

        return {
          ...item,
          _id: product._id,
          product,
          productId: product._id,
          name: product.name || item.name || "",
          image: product.image || item.image || "",
          price: Number(product.price ?? item.price ?? 0),
          quantity: Math.max(1, Number(item.quantity ?? 1)),
        };
      })
      .filter(Boolean);
  };

  const sanitizeLocalCartItems = (items = []) => {
    if (!Array.isArray(items)) return [];

    return items
      .map((item) => {
        const id = getItemId(item);
        if (!id) return null;

        return {
          ...item,
          _id: id,
          productId: id,
          quantity: Math.max(1, Number(item.quantity ?? 1)),
          price: Number(item.price ?? 0),
          name: item.name || "",
          image: item.image || "",
        };
      })
      .filter(Boolean);
  };

  const totalItems = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + Number(item.quantity || 1), 0);
  }, [cartItems]);

  const totalPrice = useMemo(() => {
    return cartItems.reduce(
      (acc, item) => acc + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );
  }, [cartItems]);

  // 🚚 DYNAMIC SHIPPING RULE: Free if totalPrice >= 499, otherwise ₹59
  const shippingFee = useMemo(() => {
    return totalPrice >= 499 || totalPrice === 0 ? 0 : 59;
  }, [totalPrice]);

  const finalTotal = useMemo(() => {
    return Math.max(0, Number(totalPrice || 0) + Number(shippingFee || 0) - Number(discount || 0));
  }, [totalPrice, shippingFee, discount]);

  const validateGuestCart = async (items) => {
    try {
      const cleanItems = sanitizeLocalCartItems(items);
      if (!cleanItems.length) return [];

      const res = await API.post("/api/cart/validate", {
        items: cleanItems.map((item) => ({
          productId: item._id,
          quantity: Number(item.quantity || 1),
        })),
      });

      return normalizeCartItems(res.data);
    } catch (error) {
      return [];
    }
  };

  const loadBackendCart = async (showErrorToast = false) => {
    try {
      const res = await API.get("/api/cart", authHeaders());
      const normalized = normalizeCartItems(res.data);
      setCartItems(normalized);
      return normalized;
    } catch (error) {
      setCartItems([]);
      if (showErrorToast) {
        toast.error(error.response?.data?.message || "Failed to load cart");
      }
      return [];
    }
  };

  const mergeGuestCartToBackend = async () => {
    try {
      const guestCart = JSON.parse(localStorage.getItem(guestCartKey) || "[]");
      const cleanGuestCart = sanitizeLocalCartItems(guestCart);

      if (!cleanGuestCart.length || !isAuthenticated()) return;

      await API.post(
        "/api/cart/merge",
        {
          items: cleanGuestCart.map((item) => ({
            productId: item._id,
            quantity: Number(item.quantity || 1),
          })),
        },
        authHeaders()
      );

      localStorage.removeItem(guestCartKey);
    } catch (error) {}
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage("Please enter a coupon code");
      setCouponApplied(false);
      return;
    }

    if (totalPrice <= 0) {
      setCouponMessage("Your cart is empty");
      setCouponApplied(false);
      return;
    }

    try {
      setCouponLoading(true);
      setCouponMessage("");

      const res = await API.post("/api/coupons/apply", {
        code: couponCode.trim().toUpperCase(),
        cartTotal: totalPrice,
      });

      const discountAmount = Number(res.data?.discount || 0);
      const nextMessage =
        discountAmount > 0
          ? `Coupon applied successfully! Saved ₹${discountAmount}`
          : "Coupon applied successfully";

      setCouponCode(couponCode.trim().toUpperCase());
      setDiscount(discountAmount);
      setCouponMessage(nextMessage);
      setCouponApplied(true);

      persistCouponState({
        nextCouponCode: couponCode.trim().toUpperCase(),
        nextDiscount: discountAmount,
        nextCouponMessage: nextMessage,
        nextCouponApplied: true,
      });

      toast.success("Coupon applied");
    } catch (error) {
      const message = error.response?.data?.message || "Invalid coupon";
      setDiscount(0);
      setCouponApplied(false);
      setCouponMessage(message);

      persistCouponState({
        nextCouponCode: couponCode.trim().toUpperCase(),
        nextDiscount: 0,
        nextCouponMessage: message,
        nextCouponApplied: false,
      });
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    const syncCartWithUser = async () => {
      const newKey = getCartStorageKey();
      setCartKey(newKey);

      if (isAuthenticated()) {
        await mergeGuestCartToBackend();
        await loadBackendCart();
      } else {
        try {
          const storedCart = JSON.parse(localStorage.getItem(newKey) || "[]");
          const validCart = await validateGuestCart(storedCart);
          setCartItems(validCart);
          localStorage.setItem(newKey, JSON.stringify(validCart));
        } catch (error) {
          setCartItems([]);
          localStorage.setItem(newKey, JSON.stringify([]));
        }
      }

      loadCouponState();
    };

    syncCartWithUser();

    window.addEventListener("storage", syncCartWithUser);
    return () => {
      window.removeEventListener("storage", syncCartWithUser);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      localStorage.setItem(cartKey, JSON.stringify(cartItems));
    }
  }, [cartItems, cartKey]);

  useEffect(() => {
    if (cartItems.length === 0) {
      removeCoupon();
      return;
    }

    if (couponApplied) {
      persistCouponState();
    }
  }, [cartItems, couponApplied]);

  const addToCart = async (product) => {
    const productId = getItemId(product);
    const qty = Math.max(1, Number(product.quantity || 1));

    if (!productId) {
      triggerAlert("error", "Error", "Invalid product selected.");
      return;
    }

    if (isAuthenticated()) {
      try {
        const res = await API.post(
          "/api/cart/add",
          {
            productId,
            quantity: qty,
          },
          authHeaders()
        );

        setCartItems(normalizeCartItems(res.data));
        triggerAlert("success", "Added to Cart!", `${product.name || "Item"} has been added to your shopping cart.`);
      } catch (error) {
        triggerAlert("error", "Failed to Add", error.response?.data?.message || "Failed to add item to cart.");
      }
      return;
    }

    const existing = cartItems.find((item) => getItemId(item) === productId);

    if (existing) {
      const updatedCart = cartItems.map((item) =>
        getItemId(item) === productId
          ? {
              ...item,
              quantity: Number(item.quantity || 1) + qty,
            }
          : item
      );

      const validCart = await validateGuestCart(updatedCart);
      setCartItems(validCart);
      triggerAlert("success", "Cart Updated", `${product.name || "Item"} quantity has been updated.`);
    } else {
      const updatedCart = [
        ...cartItems,
        {
          _id: productId,
          productId,
          name: product.name || "",
          image: product.image || "",
          price: Number(product.price || 0),
          quantity: qty,
        },
      ];

      const validCart = await validateGuestCart(updatedCart);
      setCartItems(validCart);

      if (validCart.some((item) => getItemId(item) === productId)) {
        triggerAlert("success", "Added to Cart!", `${product.name || "Item"} has been added to your shopping cart.`);
      } else {
        triggerAlert("error", "Unavailable", "This product is no longer available.");
      }
    }
  };

  const removeFromCart = async (id) => {
    const removedProduct = cartItems.find((item) => getItemId(item) === id);

    if (isAuthenticated()) {
      try {
        const res = await API.delete(`/api/cart/remove/${id}`, authHeaders());
        setCartItems(normalizeCartItems(res.data));
        if (removedProduct) {
          triggerAlert("success", "Removed from Cart", `${removedProduct.name} has been removed.`);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to remove item");
      }
      return;
    }

    const updatedCart = cartItems.filter((item) => getItemId(item) !== id);
    setCartItems(updatedCart);

    if (removedProduct) {
      triggerAlert("success", "Removed from Cart", `${removedProduct.name} has been removed.`);
    }
  };

  const increaseQty = async (id) => {
    const item = cartItems.find((cartItem) => getItemId(cartItem) === id);
    if (!item) return;

    const newQty = Number(item.quantity || 1) + 1;

    if (isAuthenticated()) {
      try {
        const res = await API.put(
          `/api/cart/update/${id}`,
          { quantity: newQty },
          authHeaders()
        );
        setCartItems(normalizeCartItems(res.data));
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update quantity");
      }
      return;
    }

    const updatedCart = cartItems.map((cartItem) =>
      getItemId(cartItem) === id ? { ...cartItem, quantity: newQty } : cartItem
    );

    const validCart = await validateGuestCart(updatedCart);
    setCartItems(validCart);
  };

  const decreaseQty = async (id) => {
    const item = cartItems.find((cartItem) => getItemId(cartItem) === id);
    if (!item) return;

    const currentQty = Number(item.quantity || 1);
    const newQty = currentQty > 1 ? currentQty - 1 : 1;

    if (isAuthenticated()) {
      try {
        const res = await API.put(
          `/api/cart/update/${id}`,
          { quantity: newQty },
          authHeaders()
        );
        setCartItems(normalizeCartItems(res.data));
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update quantity");
      }
      return;
    }

    const updatedCart = cartItems.map((cartItem) =>
      getItemId(cartItem) === id ? { ...cartItem, quantity: newQty } : cartItem
    );

    const validCart = await validateGuestCart(updatedCart);
    setCartItems(validCart);
  };

  const clearCartSilently = async () => {
    setCartItems([]);
    removeCoupon(false);

    try {
      localStorage.setItem(cartKey, JSON.stringify([]));
      localStorage.removeItem(guestCartKey);
    } catch (error) {}

    if (isAuthenticated()) {
      try {
        await API.delete("/api/cart/clear", authHeaders());
      } catch (error) {}
    }
  };

  const clearCart = async () => {
    if (isAuthenticated()) {
      try {
        await API.delete("/api/cart/clear", authHeaders());
        setCartItems([]);
        removeCoupon();
        triggerAlert("success", "Cart Cleared", "Your cart has been successfully emptied.");
      } catch (error) {
        triggerAlert("error", "Error", error.response?.data?.message || "Failed to clear cart");
      }
      return;
    }

    setCartItems([]);
    localStorage.setItem(cartKey, JSON.stringify([]));
    removeCoupon();
    triggerAlert("success", "Cart Cleared", "Your cart has been successfully emptied.");
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        setCartItems,
        addToCart,
        removeFromCart,
        increaseQty,
        decreaseQty,
        clearCart,
        clearCartSilently,
        totalItems,
        totalPrice,
        shippingFee,
        finalTotal,
        loadBackendCart,
        couponCode,
        setCouponCode,
        discount,
        couponMessage,
        couponApplied,
        couponLoading,
        applyCoupon,
        removeCoupon,
      }}
    >
      <CenterAlert {...alertConfig} onClose={closeAlert} />
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;